import React from "react";
import { Link } from "react-router-dom";
import { Card, Box, Typography, Chip } from "@mui/material";
import IndexMenu from "./IndexMenu";
import { formatJpDate } from "../../utils/dateUtils";

/**
 * 一覧用の共通カード（プロジェクト・求人など）
 * @param {object} item - 表示データ（projectまたはjob）
 * @param {string} type - "project" or "job"
 * @param {string|object} component - Linkやdivなど
 * @param {string} to - リンク先（Linkの場合）
 */
const IndexCard = ({
  item,
  type,
  csrfToken,
  onMarkedRead,
  contractExists,
  isActive,
  component: Component = Link,
  to,
  ...props
}) => {
  const publishEnd = new Date(item.publish_end_date);
  publishEnd.setHours(23, 59, 59, 999); // 終了日を当日いっぱいに

  // 本日が掲載終了日か？
  const isPublishEndToday = (() => {
    const today = new Date();
    // "YYYY-MM-DD" で比較
    const ymd = (date) =>
      `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    return ymd(today) === ymd(new Date(item.publish_end_date));
  })();

  const handleClick = () => {
    onMarkedRead?.(item.id);
  };

  return (
    <Card
      component={Component}
      to={to}
      onClick={handleClick}
      sx={{
        display: "block",
        p: 2,
        mb: 2,
        border: item.read_flag ? "1px solid #ccc" : "2px solid #838383",
        borderRadius: 2,
        position: "relative",
        cursor: "pointer",
        textDecoration: "none",
        color: "inherit",
        backgroundColor: item.read_flag ? "#EFEFEF" : "#fff",
        "&:hover": {
          backgroundColor: item.read_flag ? "#E3E3E3" : "#f9f9f9",
        },
      }}
      {...props}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {/* 左側：Chipと掲載期限 */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            flexDirection: { xs: "column", sm: "row" }, // スマホは縦、PCは横
            alignItems: { xs: "flex-start", sm: "center" }, // スマホは左寄せ
          }}
        >
          {/* 本日掲載終了 */}
          {isPublishEndToday && (
            <Chip
              label="本日掲載終了"
              color="error"
              size="small"
              sx={{ fontWeight: "bold", height: 22 }}
            />
          )}

          <Typography variant="caption" color="text.secondary">
            掲載期限 {formatJpDate(item.publish_end_date)} まで
          </Typography>
        </Box>

        {/* 右上メニュー */}
        <IndexMenu
          itemId={item.id}
          type={type}
          contractExists={contractExists}
          isActive={isActive}
        />
      </Box>

      <Typography
        variant="subtitle1"
        sx={{
          fontSize: { xs: "0.9rem", sm: "1rem" },
          lineHeight: 1.4,
          mt: isPublishEndToday ? 1 : 0,
          fontWeight: item.read_flag ? "nomal" : "bold",
        }}
      >
        {item.title}
      </Typography>

      <Typography variant="body2" mt={1}>
        {(() => {
          const elements = [];
          // 金額
          elements.push(
            type === "job"
              ? `年収 ${item.price_min}〜${item.price_max}万円`
              : `単価 ${item.price_min}〜${item.price_max}万円`
          );

          // プロジェクト：稼働形態ラベル
          if (type === "project") {
            const workStyleLabel =
              {
                on_site: "常駐",
                remote: "フルリモート",
                hybrid: "ハイブリッド",
              }[item.work_style] || null;
            if (workStyleLabel) elements.push(workStyleLabel);

            // ハイブリッド・常駐のみ勤務地を表示
            if (
              (item.work_style === "hybrid" || item.work_style === "on_site") &&
              item.prefecture_names &&
              item.prefecture_names.length > 0
            ) {
              elements.push(item.prefecture_names.join("・"));
            }
          }

          // 求人は常に勤務地を表示
          if (
            type === "job" &&
            item.prefecture_names &&
            item.prefecture_names.length > 0
          ) {
            elements.push(item.prefecture_names.join("・"));
          }

          // スキル
          const visibleSkills = (item.skills || []).filter(
            (s) => s.visible_flag !== false
          );
          if (visibleSkills.length > 0) {
            elements.push(visibleSkills.map((s) => s.skill_name).join("・"));
          }

          // ／区切りで連結
          return elements.join("／");
        })()}
      </Typography>
    </Card>
  );
};

export default IndexCard;
